import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { cartService } from '@/features/cart/services/cartService';
import { useAuth } from '@/hooks/useAuth';
import type { CartResponse } from '@/features/cart/types';

export interface UseProfilePurchasesReturn {
    completedCarts: CartResponse[];
    cartsLoading: boolean;
    cartsError: string | null;
    fetchCompletedCarts: () => Promise<void>;
}

export const useProfilePurchases = (): UseProfilePurchasesReturn => {
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [completedCarts, setCompletedCarts] = useState<CartResponse[]>([]);
    const [cartsLoading, setCartsLoading] = useState(false);
    const [cartsError, setCartsError] = useState<string | null>(null);

    // Track whether data has been fetched to prevent duplicate calls
    const cartsFetchedRef = useRef(false);

    const fetchCompletedCarts = useCallback(async () => {
        // Skip if already fetched or currently loading
        if (cartsFetchedRef.current || cartsLoading) return;

        // Proactive check: Skip API call if not authenticated
        if (!authLoading && !isAuthenticated) {
            setCartsLoading(false);
            setCartsError('Please log in to view your purchases');
            navigate('/login');
            return;
        }

        // Wait for auth check to complete
        if (authLoading) return;

        try {
            setCartsLoading(true);
            setCartsError(null);
            const data = await cartService.getAllCompleteCarts();
            setCompletedCarts(data || []);
            cartsFetchedRef.current = true;
        } catch (err) {
            console.error('Error fetching completed carts:', err);
            setCartsError('Failed to load previous purchases. Please try again.');
            toast.error('Failed to load previous purchases. Please try again.');
        } finally {
            setCartsLoading(false);
        }
    }, [authLoading, isAuthenticated, navigate, cartsLoading]);

    return {
        completedCarts,
        cartsLoading,
        cartsError,
        fetchCompletedCarts,
    };
};
